import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const QuoteCalculator = ({ recipient, onNext, onBack, quoteData, onQuoteUpdate }) => {
  const [sendAmount, setSendAmount] = useState(quoteData?.sendAmount || '1000');
  const [receiveAmount, setReceiveAmount] = useState(quoteData?.receiveAmount || '');
  const [isQuoteLocked, setIsQuoteLocked] = useState(false);
  const [lockTimeRemaining, setLockTimeRemaining] = useState(0);
  const [transferType, setTransferType] = useState(quoteData?.transferType || 'standard');

  const mockExchangeRates = {
    'USD-GBP': { rate: 0.79, markup: 0.02 },
    'USD-EUR': { rate: 0.85, markup: 0.02 },
    'USD-CAD': { rate: 1.35, markup: 0.015 }
  };

  const transferTypeOptions = [
    { value: 'standard', label: 'Standard Transfer (1-3 business days)', description: 'Lower fees, longer processing time' },
    { value: 'express', label: 'Express Transfer (Same day)', description: 'Higher fees, faster processing' },
    { value: 'instant', label: 'Instant Transfer (Within minutes)', description: 'Highest fees, immediate processing' }
  ];

  const getFeeStructure = (type, amount) => {
    const baseAmount = parseFloat(amount) || 0;
    switch (type) {
      case 'standard':
        return { fixed: 4.99, percentage: 0.005, total: 4.99 + (baseAmount * 0.005) };
      case 'express':
        return { fixed: 9.99, percentage: 0.01, total: 9.99 + (baseAmount * 0.01) };
      case 'instant':
        return { fixed: 19.99, percentage: 0.015, total: 19.99 + (baseAmount * 0.015) };
      default:
        return { fixed: 4.99, percentage: 0.005, total: 4.99 + (baseAmount * 0.005) };
    }
  };

  const calculateQuote = () => {
    const amount = parseFloat(sendAmount) || 0;
    const rateKey = `USD-${recipient?.currency || 'GBP'}`;
    const rateData = mockExchangeRates?.[rateKey] || mockExchangeRates?.['USD-GBP'];
    const fees = getFeeStructure(transferType, amount);
    
    const exchangeRate = rateData?.rate - rateData?.markup;
    const amountAfterFees = amount - fees?.total;
    const convertedAmount = amountAfterFees * exchangeRate;

    return {
      sendAmount: amount,
      receiveAmount: convertedAmount?.toFixed(2),
      exchangeRate: exchangeRate?.toFixed(4),
      marketRate: rateData?.rate?.toFixed(4),
      markup: rateData?.markup?.toFixed(4),
      fees: fees,
      transferType,
      totalCost: amount + fees?.total
    };
  };

  useEffect(() => {
    const quote = calculateQuote();
    setReceiveAmount(quote?.receiveAmount);
    onQuoteUpdate(quote);
  }, [sendAmount, transferType, recipient]);

  useEffect(() => {
    let interval;
    if (isQuoteLocked && lockTimeRemaining > 0) {
      interval = setInterval(() => {
        setLockTimeRemaining(prev => {
          if (prev <= 1) {
            setIsQuoteLocked(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isQuoteLocked, lockTimeRemaining]);

  const handleLockQuote = () => {
    setIsQuoteLocked(true);
    setLockTimeRemaining(300); // 5 minutes
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs?.toString()?.padStart(2, '0')}`;
  };

  const quote = calculateQuote();

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Get Quote</h2>
          <p className="text-muted-foreground mt-1">
            Sending to {recipient?.name} in {recipient?.country}
          </p>
        </div>
        <Button variant="ghost" onClick={onBack} iconName="ArrowLeft" iconPosition="left">
          Back
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Quote Calculator */}
        <div className="space-y-6">
          {/* Transfer Type Selection */}
          <div>
            <Select
              label="Transfer Type"
              options={transferTypeOptions}
              value={transferType}
              onChange={setTransferType}
              className="w-full"
            />
          </div>

          {/* Amount Input */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Input
                label="You Send"
                type="number"
                value={sendAmount}
                onChange={(e) => setSendAmount(e?.target?.value)}
                placeholder="0.00"
                className="text-right"
              />
              <p className="text-xs text-muted-foreground mt-1">USD</p>
            </div>
            <div>
              <Input
                label="They Receive"
                type="number"
                value={receiveAmount}
                readOnly
                className="text-right bg-muted"
              />
              <p className="text-xs text-muted-foreground mt-1">{recipient?.currency || 'GBP'}</p>
            </div>
          </div>

          {/* Exchange Rate Info */}
          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-foreground">Exchange Rate Details</h3>
              <Icon name="TrendingUp" size={16} className="text-success" />
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Market Rate:</span>
                <span className="font-medium">1 USD = {quote?.marketRate} {recipient?.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Our Rate:</span>
                <span className="font-medium">1 USD = {quote?.exchangeRate} {recipient?.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Markup:</span>
                <span className="text-warning">{(parseFloat(quote?.markup) * 100)?.toFixed(2)}%</span>
              </div>
            </div>
          </div>

          {/* Fee Breakdown */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="font-medium text-foreground mb-3">Fee Breakdown</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Transfer Fee:</span>
                <span>${quote?.fees?.fixed?.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Service Fee ({(quote?.fees?.percentage * 100)?.toFixed(1)}%):</span>
                <span>${(parseFloat(sendAmount) * quote?.fees?.percentage)?.toFixed(2)}</span>
              </div>
              <div className="border-t border-border pt-2 mt-2">
                <div className="flex justify-between font-medium">
                  <span>Total Fees:</span>
                  <span>${quote?.fees?.total?.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Quote Lock */}
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
            {!isQuoteLocked ? (
              <div className="text-center">
                <Icon name="Lock" size={24} className="text-accent mx-auto mb-2" />
                <h3 className="font-medium text-foreground mb-1">Lock This Rate</h3>
                <p className="text-sm text-muted-foreground mb-3">
                  Secure this exchange rate for 5 minutes
                </p>
                <Button variant="outline" onClick={handleLockQuote} className="w-full">
                  Lock Rate
                </Button>
              </div>
            ) : (
              <div className="text-center">
                <Icon name="Shield" size={24} className="text-success mx-auto mb-2" />
                <h3 className="font-medium text-success mb-1">Rate Locked</h3>
                <p className="text-sm text-muted-foreground mb-2">
                  Time remaining: <span className="font-mono font-medium">{formatTime(lockTimeRemaining)}</span>
                </p>
                <div className="w-full bg-border rounded-full h-2">
                  <div
                    className="bg-success h-2 rounded-full transition-all duration-1000"
                    style={{ width: `${(lockTimeRemaining / 300) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Quote Summary */}
        <div className="bg-card border border-border rounded-lg p-6">
          <h3 className="text-lg font-semibold text-foreground mb-4">Transfer Summary</h3>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Icon name="User" size={20} className="text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">{recipient?.name}</p>
                <p className="text-sm text-muted-foreground">{recipient?.email}</p>
                <p className="text-sm text-muted-foreground">{recipient?.bankName}</p>
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-border">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Send Amount:</span>
                <span className="font-medium">${parseFloat(sendAmount)?.toFixed(2)} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Fees:</span>
                <span className="font-medium">${quote?.fees?.total?.toFixed(2)} USD</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Exchange Rate:</span>
                <span className="font-medium">1 USD = {quote?.exchangeRate} {recipient?.currency}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Transfer Type:</span>
                <span className="font-medium capitalize">{transferType}</span>
              </div>
              <div className="border-t border-border pt-3">
                <div className="flex justify-between text-lg font-semibold">
                  <span>They Receive:</span>
                  <span className="text-primary">{receiveAmount} {recipient?.currency}</span>
                </div>
              </div>
            </div>

            <div className="bg-muted/50 rounded-lg p-3 mt-4">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} className="text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {transferType === 'standard' && 'Arrives in 1-3 business days'}
                  {transferType === 'express' && 'Arrives same day'}
                  {transferType === 'instant' && 'Arrives within minutes'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Continue Button */}
      <div className="flex justify-end pt-6 border-t border-border">
        <Button
          onClick={onNext}
          disabled={!sendAmount || parseFloat(sendAmount) <= 0}
          iconName="ArrowRight"
          iconPosition="right"
        >
          Continue to Compliance
        </Button>
      </div>
    </div>
  );
};

export default QuoteCalculator;